/**
 * Customer-Scoped Email Forwarding API Client
 *
 * All endpoints are gated server-side by the customer session + the
 * `email_forwarding` portal feature flag, and the domain is derived from the
 * session (never sent by the client). See handlers/customer-email-forwarding.ts.
 */

import { apiGet, apiPost, apiRequest } from './client';

// =============================================================================
// TYPES
// =============================================================================

export interface ForwardingAddress {
  address: string;
  destination: string | null;
  status: 'active' | 'pending-verification' | 'disabled';
}

export interface ForwardingStatus {
  domain: string;
  routingEnabled: boolean;
  quota: number;
  addresses: ForwardingAddress[];
}

export interface SetupResult {
  domain: string;
  decision: 'completed' | 'escalate';
  reason?: 'existing_mx_conflict' | 'quota_exceeded';
  address?: string;
  destination?: string;
  outcome?: 'active' | 'pending-verification' | 'exists' | 'failed';
  routingEnabled?: boolean;
  pendingVerification?: string[];
  message: string;
}

export interface PortalFeatures {
  email_forwarding: boolean;
  [feature: string]: boolean;
}

// =============================================================================
// API CALLS
// =============================================================================

/** The authenticated customer's portal feature flags. */
export async function getFeatures(): Promise<PortalFeatures> {
  const res = await apiGet<{ features: PortalFeatures }>('/api/customer/features');
  return res.features;
}

/** Current forwarding status for the customer's domain. */
export async function getForwardingStatus(): Promise<ForwardingStatus> {
  return apiGet<ForwardingStatus>('/api/customer/email-forwarding/status');
}

/** Create one forwarding address. May return decision:'escalate'. */
export async function setupForwarding(
  localPart: string,
  destination: string
): Promise<SetupResult> {
  return apiPost<SetupResult>('/api/customer/email-forwarding/setup', {
    localPart,
    destination,
  });
}

/** Remove one forwarding address from the customer's domain. */
export async function removeForwarding(
  localPart: string
): Promise<{ domain: string; address: string; deleted: boolean }> {
  return apiRequest('/api/customer/email-forwarding/rule', {
    method: 'DELETE',
    body: JSON.stringify({ localPart }),
  });
}
